import { mapGetters } from 'vuex'

import { shortPath } from 'app/releases'
import { marketing } from 'app/concerns'
import { determinations } from 'app/utils/accounts'
import merge from 'lodash/merge'
import isEqual from 'lodash/isEqual'
import isObject from 'lodash/isObject'
import moment from 'moment'
import snakeCase from 'lodash/snakeCase'

export const versionProvider = {
  provide() {
    return {
      version: this.version,
      urlPrefix: this.urlPrefix
    }
  },

  computed: {
    version() {
      return this.$route.meta.version
    },

    urlPrefix() {
      return `/${shortPath(this.$releases.active.version)}`
    }
  }
}

export const adFreeDocumentationPermitted = {
  computed: {
    ...mapGetters('permits', ['adFreeDocumentationVersions']),

    adFreeDocumentationPermitted() {
      return !!this.adFreeDocumentationVersions.find(({ major, minor, patch, pre }) => {
        return isEqual({ major, minor, patch, pre }, this.$releases.active.versionParts)
      })
    }
  }
}

export const hasAccessToProForActiveVersion = {
  computed: {
    ...mapGetters('permits', ['accessToPro']),

    hasAccessToProForActiveVersion() {
      return !!this.accessToPro.find(({ major, minor, patch, pre }) => {
        return isEqual({ major, minor, patch, pre }, this.$releases.active.versionParts)
      })
    }
  }
}

export const hasAccessToProPlusForActiveVersion = {
  computed: {
    ...mapGetters('permits', ['accessToProPlus']),

    hasAccessToProPlusForActiveVersion() {
      return !!this.accessToProPlus.find(({ major, minor, patch, pre }) => {
        return isEqual({ major, minor, patch, pre }, this.$releases.active.versionParts)
      })
    }
  }
}

export const SubscriptionMixin = {
  computed: {
    ...mapGetters('accounts', ['currentAccount']),

    ...mapGetters('subscriptions', ['currentSubscription']),

    marketing() {
      return marketing({ account: this.currentAccount, subscription: this.currentSubscription })
    }
  }
}

export const EventTrackingMixin = {
  computed: {
    determinationsEventProps() {
      const dt = determinations({ account: this.$account })

      return {
        has_subscription: dt.hasSubscription,
        has_active_subscription: dt.hasActiveSubscription,
        has_canceled_subscription: dt.hasCanceledSubscription,
        has_free_subscription: dt.hasFreeSubscription,
        has_pro_subscription: dt.hasProSubscription,
        is_free: dt.isFree,
        is_backer: dt.isBacker,
        is_signed_in: this.$route.meta.isSignedIn
      }
    }
  },

  methods: {
    nameSlug(name) {
      return snakeCase(name)
    },

    purchaseFlowEvent(action, params = {}) {
      this.$posthog.capture(`purchase_flow:${action}`, merge(params, this.determinationsEventProps))
    },

    iconModalContext(isUsingModal) {
      return isUsingModal ? 'icon_modal' : 'icon_landing'
    },

    supportLinkClicked(context, location = null) {
      this.$posthog.capture('contact_support_link_click', {
        context: context,
        location: location
      })
    },

    fontawesomeDownloadEvent(type, medium, version, context = null, location = null) {
      this.$posthog.capture(
        `download:fontawesome_download_button_click`,
        merge({ type: type, medium: medium, version: version, context: context }, this.determinationsEventProps)
      )
    }
  }
}

export const CountdownMixin = {
  methods: {
    timeRemaining(endDate) {
      moment.updateLocale('en', {
        relativeTime: {
          d: '1 day',
          h: '1 hour',
          m: '1 minute',
          M: '1 month',
          y: '1 year'
        }
      })

      const humanReadable = moment.parseZone(endDate).fromNow(true)

      moment.updateLocale('en', null)

      return humanReadable
    },

    isInFuture(endDate) {
      return moment.utc().isBefore(moment.parseZone(endDate))
    }
  }
}

export const StickyElementMixin = {
  data() {
    return {
      isStickyElementStuck: false
    }
  },

  async updated() {
    this.$nextTick(this.setStickyElementParentRelative)
    this.$nextTick(this.setupStickyObserver)
  },

  methods: {
    setStickyElementParentRelative() {
      const parent = this.$refs.StickyElementParent

      if (parent) {
        parent.style.position = 'relative'
      }
    },

    setupStickyObserver() {
      const sentinel = this.$refs.StickyElementSentinel

      if (sentinel) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            this.isStickyElementStuck = !entry.isIntersecting
          },
          {
            root: null,
            threshold: 0
          }
        )

        observer.observe(sentinel)
      }
    }
  }
}

// Version-specific docs prefix patterns
const VERSION_5_DOCS_PREFIX = /^\/v5\/docs/
const VERSION_6_DOCS_PREFIX = /^\/v6\/docs/
const DEFAULT_DOCS_PREFIX = /^\/docs/

export const DocsMixin = {
  methods: {
    getDocsPath(pathOrLink, options = {}) {
      const currentVersion = options.version
      const isLatestVersion = currentVersion === this.$releases.latest.together.versionParts.major
      const docsVersionPath = isLatestVersion ? this.$docs : this.$docs[`v${currentVersion}`]

      const path = typeof pathOrLink === 'object' ? pathOrLink.path : pathOrLink
      const params = typeof pathOrLink === 'object' ? { hash: pathOrLink.hash } : undefined

      // Remove version-specific docs prefix if present
      let cleanPath = path
      if (currentVersion === 5) {
        cleanPath = path.replace(VERSION_5_DOCS_PREFIX, '')
      } else if (currentVersion === 6) {
        cleanPath = path.replace(VERSION_6_DOCS_PREFIX, '')
      } else {
        cleanPath = path.replace(DEFAULT_DOCS_PREFIX, '')
      }

      // Ensure path starts with a slash for proper router resolution
      if (!cleanPath.startsWith('/')) {
        cleanPath = '/' + cleanPath
      }

      return params?.hash ? docsVersionPath(cleanPath, params) : docsVersionPath(cleanPath)
    }
  }
}

export const RouteValidationMixin = {
  methods: {
    /**
     * Validates that a route object has the expected structure for use with router.resolve()
     * @param {Object} route - The route object to validate
     * @returns {boolean} - True if the route is valid, false otherwise
     */
    validateRoute(route) {
      if (!route) return false

      return (
        isObject(route) &&
        typeof route.name === 'string' &&
        (route.params === undefined || isObject(route.params)) &&
        (route.query === undefined || isObject(route.query))
      )
    }
  }
}
